import { c as _c } from "react/compiler-runtime";
import { History, LocationDescriptorObject } from 'history';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as qs from 'utils/queryParams';
import { SerializerTypes, StringSerializer } from './paramsSerializer';
export enum UpdateType {
  ReplaceIn = 'replaceIn',
  PushIn = 'pushIn',
  Replace = 'replace',
  Push = 'push',
}

/**
 * remove search params that are null or an empty string.
 */
export const filterNullOrEmpty = (searchObj: Record<string, any>) => Object.keys(searchObj).reduce<Record<string, any>>((filteredSearchObj, searchKey) => {
  const value = searchObj[searchKey];

  // if it isn't null or empty string, add it to the accumulated obj
  if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
    filteredSearchObj[searchKey] = value;
  }
  return filteredSearchObj;
}, {});
const createLocationSearch = (location: LocationDescriptorObject, search: Record<string, any>) => ({
  ...location,
  search: qs.stringify(filterNullOrEmpty(search))
});
const updateLocationSearch = (location: LocationDescriptorObject, newSearchValues: Record<string, any>) => {
  const prevSearch = qs.parse(location.search);
  return createLocationSearch(location, {
    ...prevSearch,
    ...newSearchValues
  });
};

/**
 * Updates the URL to match the specified search query changes.
 * If replaceIn or pushIn are used as the updateType, then parameters
 * not specified in newSearchValues are retained. If replace or push
 * are used as the updateType, then parameters not specified in newSearchValues
 * NOT are retained.
 */

export const updateUrlSearch = (newSearchValues: Record<string, any>, location: LocationDescriptorObject, history: History, updateType: UpdateType = UpdateType.ReplaceIn) => {
  switch (updateType) {
    case UpdateType.ReplaceIn:
      history.replace(updateLocationSearch(location, newSearchValues));
      break;
    case UpdateType.PushIn:
      history.push(updateLocationSearch(location, newSearchValues));
      break;
    case UpdateType.Replace:
      history.replace(createLocationSearch(location, newSearchValues));
      break;
    case UpdateType.Push:
      history.push(createLocationSearch(location, newSearchValues));
      break;
    default:
  }
};

/**
 * Decodes a value using the serializer with the default decoder set to
 * for Strings
 */
export function decodeValue<T extends DecodeReturnType>(encodedValue: string, serializer: SerializerTypes = StringSerializer): T {
  return serializer.decode(encodedValue) as T;
}
type DecodeReturnType = ReturnType<SerializerTypes['decode']>;

/**
 * Thanks to our inspiration: https://github.com/pbeshai/use-query-params
 *
 * This hook is used to handle a SINGLE query parameter in our url. Given a name, a serializer
 * config that defines how to serialize our parameter this hook will return the decoded
 * value and a setter function to update the query parameter.
 *
 * The setter takes two arguments (newValue, UpdateType) where updateType
 * is one of 'replace' | 'replaceIn' | 'push' | 'pushIn', defaulting to
 * 'replaceIn'.
 *
 * [serializer] example
 * NumberSerializer
 * where the serializer are available in paramsSerializer.js and are used to be able to
 * keep the original query param type trough the process because we need to convert
 * them to string to insert in the url
 *
 * [searchQuery] (OPTIONAL) example
 * You may optionally pass in a rawQuery searchQuery, otherwise the query is derived
 * from the location available in the react-router hook.
 *
 * @param name - Identifier of the query parameter
 * @param serializer - Serializer configuration that defined how to encode and decode the query param
 * @param searchQuery - already defined search query to use as an initial value
 */
export function useQueryParam(name, t0, searchQuery) {
  const $ = _c(14);
  const serializer = t0 === undefined ? StringSerializer : t0;
  const history = useHistory();
  const location = useLocation();
  let t1;
  if ($[0] !== location.search || $[1] !== searchQuery) {
    t1 = searchQuery || qs.parse(location.search) || {};
    $[0] = location.search;
    $[1] = searchQuery;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const parsedSearch = t1;
  const [query, setQuery] = React.useState(parsedSearch);
  const [prevParsedName, setPrevParsedName] = React.useState(parsedSearch[name]);
  if (parsedSearch[name] !== prevParsedName) {
    setPrevParsedName(parsedSearch[name]);
    setQuery(parsedSearch);
  }
  const encodedValue = query[name];
  let t2;
  bb0: {
    if (encodedValue === null) {
      t2 = undefined;
      break bb0;
    }
    let t3;
    if ($[3] !== encodedValue || $[4] !== serializer) {
      t3 = decodeValue(encodedValue, serializer);
      $[3] = encodedValue;
      $[4] = serializer;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const decodedValue = t2;
  let t3;
  if ($[6] !== history || $[7] !== location || $[8] !== name || $[9] !== serializer) {
    t3 = (newValue, updateType) => {
      const newEncodedValue = serializer.encode(newValue);
      setQuery({
        [name]: newEncodedValue
      });
      updateUrlSearch({
        [name]: newEncodedValue
      }, location, history, updateType);
    };
    $[6] = history;
    $[7] = location;
    $[8] = name;
    $[9] = serializer;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const setValue = t3;
  let t4;
  if ($[11] !== decodedValue || $[12] !== setValue) {
    t4 = [decodedValue, setValue];
    $[11] = decodedValue;
    $[12] = setValue;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export default useQueryParam;